import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";

// Full-screen click-to-enlarge viewer for gallery images / boards.
// items: [{ url, caption, type }], index: active index (or -1 to hide).
export default function Lightbox({ items = [], index = -1, onClose, onIndex }) {
  const open = index >= 0 && index < items.length;
  const [mag, setMag] = useState(false);

  useEffect(() => { setMag(false); }, [index]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onIndex(Math.min(items.length - 1, index + 1));
      else if (e.key === "ArrowLeft") onIndex(Math.max(0, index - 1));
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, index, items.length, onClose, onIndex]);

  if (!open) return null;
  const item = items[index];

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex flex-col"
      data-lenis-prevent data-testid="image-lightbox"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-5 md:px-8 py-4 shrink-0">
        <span className="font-mono text-[11px] uppercase tracking-widest text-muted2">
          {item.caption ? item.caption : "View"}, {index + 1} / {items.length}
        </span>
        <div className="flex items-center gap-3">
          {item.type !== "video" && (
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-muted2/70">Tap image to zoom</span>
          )}
          <button onClick={onClose} data-testid="lightbox-close"
            className="rounded-full border border-white/20 p-2 text-white hover:border-lime hover:text-lime transition-colors">
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="relative flex-1 min-h-0 flex items-start justify-center px-0 md:px-16 pb-6" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onIndex(Math.max(0, index - 1))}
          disabled={index === 0}
          data-testid="lightbox-prev"
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 rounded-full border border-white/20 bg-black/50 backdrop-blur p-2 md:p-3 text-white hover:border-lime hover:text-lime disabled:opacity-30 transition-colors"
        >
          <ChevronLeft size={20} />
        </button>

        <div className={`h-full w-full py-2 ${mag ? "overflow-auto" : "overflow-y-auto"}`} data-lenis-prevent>
          {item.type === "video"
            ? <video src={mediaUrl(item.url)} controls autoPlay playsInline className="mx-auto block max-h-[82vh] max-w-[96vw] w-auto h-auto rounded-lg" />
            : <img
                src={mediaUrl(item.url)}
                alt={item.caption || "board"}
                data-testid="lightbox-image"
                data-cursor="hover"
                onClick={() => setMag((m) => !m)}
                style={mag ? { width: "min(1800px, 220vw)", maxWidth: "none" } : undefined}
                className={`mx-auto block h-auto rounded-lg shadow-2xl transition-[width] duration-300 ${mag ? "cursor-zoom-out" : "max-w-[96vw] md:max-w-[1000px] cursor-zoom-in"}`}
              />}
        </div>

        <button
          onClick={() => onIndex(Math.min(items.length - 1, index + 1))}
          disabled={index === items.length - 1}
          data-testid="lightbox-next"
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 rounded-full border border-white/20 bg-black/50 backdrop-blur p-2 md:p-3 text-white hover:border-lime hover:text-lime disabled:opacity-30 transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>,
    document.body
  );
}
